import { ApiError } from './auth';
import { API_BASE_URL } from '@/config/apiConfig';
import { createApiClient, isAxiosError, isCancel } from './client';
import type { PlayerRead } from './schemas';

export interface ClubSeasonScope {
  competition_id: number;
  competition_name: string;
  competition_short_name: string;
  season_identifier: string;
}

export interface ClubPlayerLeader extends PlayerRead {
  fantasy_points: number;
  fppg?: number | null;
}

export interface ClubResearchRow extends ClubSeasonScope {
  club_id: number;
  club_name: string;
  games_played: number;
  points_for: number;
  points_against: number;
  net_points: number;
  points_for_per_match: number | null;
  points_against_per_match: number | null;
  rating?: number | null;
  power_rank: number | null;
  offense_rank: number | null;
  defense_rank: number | null;
  stats_for: Record<string, number>;
  stats_against: Record<string, number>;
  stat_ranks_for: Record<string, number | null>;
  stat_ranks_against: Record<string, number | null>;
  top_players?: ClubPlayerLeader[];
  top_players_in_competition?: ClubPlayerLeader[];
}

export interface ClubResearchResponse {
  clubs: ClubResearchRow[];
  seasons: ClubSeasonScope[];
  as_of: string;
  pending_matches: number;
}

export interface ClubPlayer extends PlayerRead {
  fantasy_points: number;
  fppg?: number | null;
  stat_counts: Record<string, number>;
  stat_points: Record<string, number>;
}

export interface ClubPlayersResponse {
  club_id: number;
  club_name: string;
  competition_id: number;
  competition_name: string;
  players: ClubPlayer[];
  seasons: ClubSeasonScope[];
  as_of: string;
}

const client = createApiClient({ baseURL: API_BASE_URL });

async function read<T>(path: string, competitionIds: number[], signal?: AbortSignal, includeLeaders = false): Promise<T> {
  const params = new URLSearchParams();
  for (const id of [...new Set(competitionIds)].sort((a, b) => a - b)) params.append('competition_ids', String(id));
  if (includeLeaders) params.set('include_leaders', 'true');
  try {
    return (await client.get<T>(`${path}?${params}`, { signal })).data;
  } catch (error: unknown) {
    if (isCancel(error)) throw error;
    throw new ApiError('Unable to load team research.', isAxiosError(error) ? error.response?.status : undefined, { cause: error });
  }
}

export const fetchClubResearch = (competitionIds: number[], signal?: AbortSignal, includeLeaders = false) =>
  read<ClubResearchResponse>('/stats/clubs/current-season', competitionIds, signal, includeLeaders);

export const fetchClubPlayers = (clubId: number, competitionIds: number[], signal?: AbortSignal) =>
  read<ClubPlayersResponse>(`/stats/clubs/${clubId}/current-season/players`, competitionIds, signal);
